/**
 * UploadContext — global upload queue that persists across page navigation.
 *
 * Uploads are processed sequentially. Each job tracks bytes transferred,
 * elapsed time, and an ETA computed from upload speed. XHR refs are stored
 * in a Map keyed by job ID so individual jobs can be cancelled.
 *
 * The auth token is passed per-enqueue for the upload itself. The status poll
 * that follows reads the CURRENT token from the auth context on every tick
 * instead: transcoding can outlast the token captured at enqueue (KAN-139).
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { loadWebConfig } from '@kantera/config';
import { useAuth } from '../hooks/useAuth';
import { resolveVideoPollResult } from './uploadPolling';
import { buildCreateVideoBody } from './uploadRequest';

const webConfig = loadWebConfig();
const API_URL = webConfig.apiUrl;
// W-5 (2026-05-03): client-side mirror of the FEATURE_VIDEO_ANALYSIS flag.
// Per Taylor decision 2026-05-03, this flips ON at launch (2026-07-15).
// While off, /analyze is not invoked and no Replicate billing accrues; once
// on, every successful upload kicks off YOLOv8 inference. The handler at
// apps/api/src/routes/analysis.ts:110 is gated server-side anyway, but we
// also gate client-side to avoid a known-to-fail call.
const FEATURE_VIDEO_ANALYSIS = webConfig.featureVideoAnalysis ?? false;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * 'status_unknown': the file uploaded and processing was triggered, but the status
 * poll was refused (401/403/404/429) and stopped. NOT a failure — the video most
 * likely finishes on its own; the page just can no longer tell.
 */
export type UploadPhase =
  'queued' | 'uploading' | 'processing' | 'ready' | 'error' | 'status_unknown';

/**
 * Poll answers that will not change by asking again in 3 s. Polling on through them
 * spent the user's per-minute rate-limit budget on a request that could not succeed,
 * for as long as the tab stayed open (KAN-139). 5xx and network errors are transient
 * and are still retried.
 */
const POLL_STOP_STATUSES = new Set([401, 403, 404, 429]);

export interface UploadJob {
  id: string;
  file: File;
  title: string;
  videoId: string | null;
  phase: UploadPhase;
  /** 0–100 */
  progress: number;
  uploadedBytes: number;
  totalBytes: number;
  /** ms timestamp when the PUT to storage started */
  startedAt: number | null;
  /** null = not enough data yet or already complete */
  estimatedSecondsRemaining: number | null;
  errorMessage: string | null;
  /**
   * Machine-readable failure code from the create call, when there was one.
   * Lets a caller distinguish a consent refusal from a generic upload failure —
   * 'COPPA_PHOTO_VIDEO_CONSENT_REQUIRED' must never render as "upload failed".
   */
  errorCode: string | null;
  /**
   * BN-115, KAN-221: the match this footage belongs to. Required: every video names its event
   * (Taylor 2026-09-27), and POST /api/videos refuses a video with no match. An upload enqueued
   * without one fails on the client with a clear message and never reaches the API.
   */
  matchId: string;
  /**
   * W-5: state of the post-upload Replicate analyze trigger.
   *   - 'disabled'         FEATURE_VIDEO_ANALYSIS is off; no /analyze call attempted
   *   - 'queued'           /analyze returned 202; Replicate job queued server-side
   *   - 'consent_blocked'  /analyze returned 403 ml_analysis_consent_required
   *   - 'failed'           /analyze returned non-2xx for any other reason
   *   - null               not yet attempted (job still uploading/processing)
   */
  analyzeStatus: 'disabled' | 'queued' | 'consent_blocked' | 'failed' | null;
  /** Detail message paired with analyzeStatus (especially for consent_blocked / failed). */
  analyzeMessage: string | null;
}

export interface UploadContextType {
  jobs: UploadJob[];
  /** `matchId` is required (KAN-221): the match this footage is of. */
  enqueueUpload: (file: File, title: string, accessToken: string, matchId: string) => void;
  cancelUpload: (jobId: string) => void;
  clearCompleted: () => void;
  activeCount: number;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const UploadContext = createContext<UploadContextType | null>(null);

export function useUpload(): UploadContextType {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUpload must be used inside <UploadProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// ETA helpers
// ---------------------------------------------------------------------------

/** Format estimated seconds remaining into a human-readable string. */
export function formatEta(seconds: number | null, startedAt: number | null): string {
  if (startedAt === null) return '';
  const elapsedMs = Date.now() - startedAt;
  if (elapsedMs < 5000) return 'Calculating...';
  if (seconds === null) return '';

  if (seconds < 60) return `~${Math.round(seconds)}s remaining`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `~${m}:${s.toString().padStart(2, '0')} remaining`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `~${h}h ${m}m remaining`;
}

// ---------------------------------------------------------------------------
// Internal state ref shapes
// ---------------------------------------------------------------------------

interface InternalJobRefs {
  xhr: XMLHttpRequest | null;
  pollInterval: ReturnType<typeof setInterval> | null;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<UploadJob[]>([]);

  // Map of jobId → { xhr, pollInterval } — not in state so they don't trigger renders
  const refs = useRef<Map<string, InternalJobRefs>>(new Map());

  // Tracks which job is currently running so we only process one at a time
  const processingRef = useRef<string | null>(null);

  // The poll outlives the page that queued the upload and runs for as long as
  // transcoding takes — past an hourly token refresh. Each tick reads this, never
  // the token captured at enqueue.
  const { accessToken: currentAccessToken } = useAuth();
  const accessTokenRef = useRef<string | null>(currentAccessToken);
  useEffect(() => {
    accessTokenRef.current = currentAccessToken;
  }, [currentAccessToken]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const setJob = useCallback((id: string, patch: Partial<UploadJob>) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  const stopJob = useCallback((id: string) => {
    const r = refs.current.get(id);
    if (!r) return;
    r.xhr?.abort();
    if (r.pollInterval) clearInterval(r.pollInterval);
    refs.current.delete(id);
  }, []);

  // ---------------------------------------------------------------------------
  // Poll a single video until ready or failed
  // ---------------------------------------------------------------------------

  const startPolling = useCallback(
    (jobId: string, videoId: string) => {
      const r = refs.current.get(jobId);
      if (!r) return;
      if (r.pollInterval) clearInterval(r.pollInterval);

      /** Stop polling for good: the status can no longer be checked from here. */
      const giveUp = () => {
        const ref = refs.current.get(jobId);
        if (ref?.pollInterval) clearInterval(ref.pollInterval);
        if (ref) ref.pollInterval = null;
        setJob(jobId, { phase: 'status_unknown', estimatedSecondsRemaining: null });
        // Release the queue — the next upload must not wait on a poll that has stopped.
        processingRef.current = null;
      };

      const poll = async () => {
        const accessToken = accessTokenRef.current;
        // Signed out since the upload: there is no one to check on behalf of.
        if (!accessToken) {
          giveUp();
          return;
        }
        try {
          const res = await fetch(`${API_URL}/api/videos/${videoId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (!res.ok) {
            if (POLL_STOP_STATUSES.has(res.status)) giveUp();
            return;
          }
          // GET /api/videos/:id returns the video BARE — status is top-level, not body.data.status.
          const result = resolveVideoPollResult(await res.json());
          if (result) {
            const ref = refs.current.get(jobId);
            if (ref?.pollInterval) clearInterval(ref.pollInterval);
            if (ref) ref.pollInterval = null;
            setJob(jobId, {
              phase: result.phase,
              estimatedSecondsRemaining: null,
              errorMessage: result.errorMessage,
            });
            processingRef.current = null;
          }
        } catch {
          // silent — keep polling
        }
      };

      const interval = setInterval(() => void poll(), 3000);
      r.pollInterval = interval;
    },
    [setJob],
  );

  // ---------------------------------------------------------------------------
  // Run a single upload job end-to-end
  // ---------------------------------------------------------------------------

  const runJob = useCallback(
    async (job: UploadJob, accessToken: string) => {
      processingRef.current = job.id;

      refs.current.set(job.id, { xhr: null, pollInterval: null });

      const startedAt = Date.now();
      setJob(job.id, { phase: 'uploading', startedAt, progress: 0, uploadedBytes: 0 });

      try {
        // Step 1 — create video record, get uploadUrl
        const createRes = await fetch(`${API_URL}/api/videos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(
            buildCreateVideoBody({
              title: job.title,
              fileName: job.file.name,
              contentType: job.file.type || 'video/mp4',
              totalBytes: job.file.size,
              matchId: job.matchId,
            }),
          ),
        });

        if (!createRes.ok) {
          const errBody = (await createRes.json().catch(() => ({}))) as {
            message?: string;
            error?: string;
            code?: string;
          };
          // Record the code before throwing so the UI can tell a consent refusal
          // apart from a transport failure.
          if (errBody.code) setJob(job.id, { errorCode: errBody.code });
          throw new Error(errBody.message ?? errBody.error ?? `API ${createRes.status}`);
        }

        const createBody = (await createRes.json()) as {
          videoId?: string;
          uploadUrl?: string;
          data?: { videoId: string; uploadUrl: string };
        };

        const videoId = createBody.videoId ?? createBody.data?.videoId;
        const uploadUrl = createBody.uploadUrl ?? createBody.data?.uploadUrl;

        if (!videoId || !uploadUrl) {
          throw new Error('Invalid API response — missing videoId or uploadUrl');
        }

        setJob(job.id, { videoId });

        // Step 2 — PUT raw file to signed URL via XHR for progress events
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          const ref = refs.current.get(job.id);
          if (ref) ref.xhr = xhr;

          xhr.upload.addEventListener('progress', (evt) => {
            if (!evt.lengthComputable) return;
            const uploaded = evt.loaded;
            const total = evt.total;
            const pct = (uploaded / total) * 100;

            // ETA: (elapsed / fraction_done) * (1 - fraction_done)
            const elapsedSec = (Date.now() - startedAt) / 1000;
            const fractionDone = uploaded / total;
            const eta =
              fractionDone > 0.01 ? (elapsedSec / fractionDone) * (1 - fractionDone) : null;

            setJob(job.id, {
              progress: pct,
              uploadedBytes: uploaded,
              totalBytes: total,
              estimatedSecondsRemaining: eta,
            });
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`Upload PUT failed: ${xhr.status}`));
            }
          });
          xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
          xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', job.file.type || 'video/mp4');
          xhr.send(job.file);
        });

        const ref = refs.current.get(job.id);
        if (ref) ref.xhr = null;

        // Step 3 — trigger processing
        setJob(job.id, { phase: 'processing', progress: 100, estimatedSecondsRemaining: null });

        const processRes = await fetch(`${API_URL}/api/videos/${videoId}/process`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        // Non-fatal if the endpoint doesn't exist — some pipelines auto-trigger
        if (!processRes.ok && processRes.status !== 404) {
          throw new Error(`Process trigger failed: ${processRes.status}`);
        }

        // Step 3.5 — W-5 (2026-05-03): kick off Replicate YOLOv8 analysis.
        //
        // Before W-5 the upload pipeline ended at /process (which just flips
        // status to 'ready'); the actual ML analyze endpoint at
        // apps/api/src/routes/analysis.ts:110 was never invoked from the web
        // app, so every uploaded video sat unanalyzed and the analysis tab
        // showed empty heatmaps/events/possession/tracking.
        //
        // The /analyze call is best-effort and non-fatal: the upload itself
        // already succeeded; the analysis is a follow-on. Failures are
        // surfaced via job.analyzeStatus + analyzeMessage so the UI can
        // render a small badge ("AI analysis queued" / "consent required" /
        // "analysis failed") without blocking the user from seeing the video.
        if (!FEATURE_VIDEO_ANALYSIS) {
          setJob(job.id, { analyzeStatus: 'disabled', analyzeMessage: null });
        } else {
          try {
            const analyzeRes = await fetch(`${API_URL}/api/videos/${videoId}/analyze`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({}),
            });

            if (analyzeRes.status === 202) {
              setJob(job.id, { analyzeStatus: 'queued', analyzeMessage: null });
            } else if (analyzeRes.status === 403) {
              // KI-042 ml_analysis_consent gate — surface specific reason.
              const errBody = (await analyzeRes.json().catch(() => ({}))) as {
                error?: string;
                message?: string;
              };
              const msg =
                errBody.message ??
                'AI analysis blocked: parental ML consent required for one or more under-13 players on this match.';
              setJob(job.id, { analyzeStatus: 'consent_blocked', analyzeMessage: msg });
            } else {
              const errBody = (await analyzeRes.json().catch(() => ({}))) as {
                error?: string;
                message?: string;
              };
              setJob(job.id, {
                analyzeStatus: 'failed',
                analyzeMessage:
                  errBody.message ?? `Analyze trigger failed (HTTP ${analyzeRes.status})`,
              });
            }
          } catch (err) {
            // Network/transport failure — non-fatal for the upload job itself
            const msg = err instanceof Error ? err.message : 'Analyze trigger failed';
            setJob(job.id, { analyzeStatus: 'failed', analyzeMessage: msg });
          }
        }

        // Step 4 — poll for completion
        startPolling(job.id, videoId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        if (msg === 'Upload cancelled') {
          setJob(job.id, {
            phase: 'error',
            errorMessage: 'Cancelled',
            estimatedSecondsRemaining: null,
          });
        } else {
          setJob(job.id, { phase: 'error', errorMessage: msg, estimatedSecondsRemaining: null });
        }
        processingRef.current = null;
        refs.current.delete(job.id);
      }
    },
    [setJob, startPolling],
  );

  // ---------------------------------------------------------------------------
  // Queue processor — watches for queued jobs and starts the next one
  // ---------------------------------------------------------------------------

  // We keep a stable ref to accessTokens so the effect can read the latest value
  // without re-subscribing every time. Auth tokens are passed per-enqueue and
  // stored temporarily in this map.
  const tokenMap = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (processingRef.current !== null) return;

    const nextJob = jobs.find((j) => j.phase === 'queued');
    if (!nextJob) return;

    const token = tokenMap.current.get(nextJob.id);
    if (!token) return;

    void runJob(nextJob, token);
  }, [jobs, runJob]);

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  const enqueueUpload = useCallback(
    (file: File, title: string, accessToken: string, matchId: string) => {
      const id = `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      tokenMap.current.set(id, accessToken);

      const newJob: UploadJob = {
        id,
        file,
        title,
        videoId: null,
        phase: 'queued',
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
        startedAt: null,
        estimatedSecondsRemaining: null,
        errorMessage: null,
        errorCode: null,
        matchId: matchId ?? '',
        analyzeStatus: null,
        analyzeMessage: null,
      };

      setJobs((prev) => [...prev, newJob]);
    },
    [],
  );

  const cancelUpload = useCallback(
    (jobId: string) => {
      stopJob(jobId);
      tokenMap.current.delete(jobId);
      setJob(jobId, { phase: 'error', errorMessage: 'Cancelled', estimatedSecondsRemaining: null });
      if (processingRef.current === jobId) processingRef.current = null;
    },
    [stopJob, setJob],
  );

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter(
        (j) => j.phase !== 'ready' && j.phase !== 'error' && j.phase !== 'status_unknown',
      ),
    );
  }, []);

  const activeCount = jobs.filter(
    (j) => j.phase === 'queued' || j.phase === 'uploading' || j.phase === 'processing',
  ).length;

  // ---------------------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const currentRefs = refs.current;
    return () => {
      for (const [id] of currentRefs) {
        stopJob(id);
      }
    };
  }, [stopJob]);

  return (
    <UploadContext.Provider
      value={{ jobs, enqueueUpload, cancelUpload, clearCompleted, activeCount }}
    >
      {children}
    </UploadContext.Provider>
  );
};
